/**
 * Cookie Consent Utilities
 *
 * GDPR-compliant cookie consent management for analytics cookies.
 *
 * @example
 * ```tsx
 * // 1. Wrap your app with CookieConsentProvider (before AnalyticsProvider)
 * import { CookieConsentProvider } from "@punto/frontend-shared/providers/cookie-consent-provider";
 * import { CookieConsentBanner } from "@punto/frontend-shared/components/cookie-consent-banner";
 *
 * <CookieConsentProvider>
 *   <AnalyticsProvider>
 *     <App />
 *   </AnalyticsProvider>
 *   <CookieConsentBanner />
 * </CookieConsentProvider>
 *
 * // 2. Use the hook to access consent state
 * import { useCookieConsent } from "@punto/frontend-shared/hooks/use-cookie-consent";
 *
 * const { consentGiven, acceptCookies, rejectCookies } = useCookieConsent();
 *
 * // 3. Link to cookie policy
 * import { COOKIE_POLICY_URL } from "@punto/frontend-shared/utils/cookie-consent";
 *
 * <a href={COOKIE_POLICY_URL}>Cookie Policy</a>
 * ```
 */
import Cookies from "js-cookie";

export const COOKIE_POLICY_URL = "https://www.punto.ai/legal/cookies";

const COOKIE_NAME = "punto_cookies";
const COOKIE_VERSION = "1";
const COOKIE_EXPIRES_DAYS = 365;

interface CookieConsent {
  analytics: boolean;
  timestamp: string;
  version: string;
}

export const getCookieConsent = (): CookieConsent | null => {
  const value = Cookies.get(COOKIE_NAME);
  if (!value) return null;

  try {
    const consent = JSON.parse(value) as CookieConsent;
    if (consent.version !== COOKIE_VERSION) return null;
    return consent;
  } catch {
    return null;
  }
};

export const setCookieConsent = (analytics: boolean): void => {
  const consent: CookieConsent = {
    analytics,
    timestamp: new Date().toISOString(),
    version: COOKIE_VERSION,
  };

  Cookies.set(COOKIE_NAME, JSON.stringify(consent), {
    expires: COOKIE_EXPIRES_DAYS,
    sameSite: "Lax",
  });
};

export const removeCookieConsent = (): void => {
  Cookies.remove(COOKIE_NAME);
};
